import { Component, EventEmitter, Input, OnDestroy, Output, TemplateRef } from "@angular/core";
import { SearchErrorWithResults, SearchQuery } from "common/autoCompleteSearch/search-query";
import { Subject, Subscription } from "rxjs";

export type CreateAllowedCallback = (args: unknown, args2: unknown) => boolean;

const MAX_SEARCH_TERM_LENGTH = Number.MAX_SAFE_INTEGER;
const MIN_SEARCH_TERM_LENGTH = 1;

@Component({
	selector: "stages-auto-complete-search",
	templateUrl: "auto-complete-search.component.html",
})
export class AutoCompleteSearchComponent<T> implements OnDestroy {
	@Input()
	backButtonActive: boolean = true;

	@Input()
	browseButtonActive: boolean = false;

	@Input()
	createDependendElementAllowedCallback?: CreateAllowedCallback;

	@Input()
	dropDownClass?: string;

	@Input()
	focusOnInit: boolean = false;

	@Input()
	maxSearchTermLength: number = MAX_SEARCH_TERM_LENGTH;

	@Input()
	minSearchTermLength: number = MIN_SEARCH_TERM_LENGTH;

	@Input()
	inputIconClasses?: string[];

	@Input()
	isDense: boolean = false;

	@Input() set isInSubordinatedElementsMode(isInSubordinatedElementsMode: boolean) {
		this._isInSubordinatedElementsMode = isInSubordinatedElementsMode;
		if (isInSubordinatedElementsMode || this.currentTerm.length >= this.minSearchTermLength) {
			this.triggerOnSearchInput$.next(this.currentTerm);
		}
	}

	@Input()
	placeholder: string = "";

	@Input()
	query!: SearchQuery<T>;

	@Input()
	searchResultSeparatorTemplate?: TemplateRef<unknown>;

	@Input()
	searchResultTemplate?: TemplateRef<unknown>;

	@Output()
	readonly backButtonPressed = new EventEmitter<void>();

	@Output()
	readonly cancelled = new EventEmitter<void>();

	@Output()
	readonly closeButtonPressed = new EventEmitter<void>();

	@Output()
	readonly focusLost = new EventEmitter<T>();

	@Output()
	readonly openBrowserButtonPressed = new EventEmitter<void>();

	@Output()
	readonly submitted = new EventEmitter<T>();

	_isInSubordinatedElementsMode: boolean = false;

	currentTerm: string = "";

	loading: boolean = false;

	oldTerm?: string;

	results: T[] = [];
	subordinatedResults?: T[];

	searchEngineAvailable = true;

	selectedParentItem?: T;

	private subscriptions = new Subscription();

	private destroy$: Subject<boolean> = new Subject<boolean>();

	private triggerOnSearchInput$: Subject<string> = new Subject<string>();

	constructor() {
		// eslint-disable-next-line rxjs/no-async-subscribe -- We want to subscribe with async function
		this.subscriptions.add(this.triggerOnSearchInput$.subscribe(async (term) => this.onSearchInput(term)));
	}

	async onSearchInput(term: string): Promise<void> {
		this.currentTerm = term;
		this.loading = true;
		try {
			await this.executeSearchQuery(term);
			this.searchEngineAvailable = true;
		} catch (e: unknown) {
			this.passErrorToResults(e);
			this.searchEngineAvailable = false;
		} finally {
			this.loading = false;
		}
	}

	async executeSearchQuery(term: string): Promise<void> {
		if (this._isInSubordinatedElementsMode) {
			this.subordinatedResults = await this.query.run(term);
		} else {
			this.results = await this.query.run(term);
		}
	}

	passErrorToResults(error: unknown): void {
		if (this._isInSubordinatedElementsMode) {
			this.subordinatedResults = error instanceof SearchErrorWithResults ? error.results : [];
		} else {
			this.results = error instanceof SearchErrorWithResults ? error.results : [];
		}
	}

	back(): void {
		this.selectedParentItem = undefined;
		this.subordinatedResults = undefined;
		if (this.oldTerm) {
			this.currentTerm = this.oldTerm;
		}
		this.backButtonPressed.emit();
	}

	isCreateAllowed(item: unknown): boolean {
		if (this.createDependendElementAllowedCallback && this._isInSubordinatedElementsMode) {
			return this.createDependendElementAllowedCallback(item, this.selectedParentItem);
		}
		return true;
	}

	itemSelected(item: T): void {
		this.selectedParentItem = item;
		this.oldTerm = this.currentTerm;
		this.currentTerm = "";
		this.submitted.emit(item);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.destroy$.next(true);
	}
}
